import React, {createContext, useContext, useState} from "react";
import UserContext from "../../common/UserContext";
import {GeneralContext, SelectedProductContext} from "../FSARequestLicenseContext";
import {useLocalization} from "@progress/kendo-react-intl";
import {GridColumn} from "@progress/kendo-react-grid";
import {Popover} from "@progress/kendo-react-tooltip";
import {Calendar} from "@progress/kendo-react-dateinputs";
import {
    EndDatePickerCell,
    SelectDateHeaderCell,
    StartDatePickerCell
} from "./DatePickerCell";
import {
    descriptionKey, endDateKey,
    licenseTypeKey,
    mainMessages,
    productNumberKey,
    qtyKey, startDateKey, statusKey, totalKey
} from "../../../assets/text/MultilingualText";
import {ColumnMenu, NoWrapCell} from "../../common/Grid";
import {LicenseTypeCell} from "./LicenseTypeCell";
import {StatusCell} from "./StatusCell";
import {DeleteCell} from "./DeleteCell";
import {TotalCell} from "./TotalCell";
import GenericSelectGrid from "../../common/AssignHost/GenericSelectGrid";
import {SelectedQtyCell} from "./SelectedQtyCell";

export const DateGridContext = createContext({});

export const SelectedProductsGrid = (props) => {
    const {
        siteLanguageDefault,
    } = useContext(UserContext);

    const {
        initialGridState,
        uniqueID,
        productNumKey,
        licenseStartDateKey,
        licenseEndDateKey,
        headerInfo,
        minDate,
        minDurationDate,
        maxDate,
        validateDates
    } = useContext(GeneralContext);

    const {
        selectID,
        selectedProducts: items,
        setSelectedProducts: setItems,
        selectedGridState: gridState,
        setSelectedGridState: setGridState,
        selectHeaderCheck,
        setSelectHeaderCheck,
        unsavedChanges,
        setUnsavedChanges
    } = useContext(SelectedProductContext);

    const {
        showGenericError,
        setShowGenericError,
    } = props

    const localization = useLocalization();

    const onItemChange = (e) => {
        let newData = items.map((item) => {
            if (item[uniqueID] === e.dataItem[uniqueID]) {
                if (e.field === 'license_type') {
                    let selectExist = false
                    item.license_type.forEach(type => {
                        type.selected = false
                        if (type.text === 'Select') {
                            selectExist = true
                        }
                        if (type.text === e.value.text) {
                            type.selected = true
                            item['selected_license_type_text'] = type.text
                            item['selected_product_id'] = type.product_id
                        }
                    })

                    //remove the default select option
                    if (selectExist) {
                        item.license_type.shift()
                    }
                } else if (e.field === 'quantity') {
                    let minQty = e.dataItem['saved_assigned_quantity'] > 1 ? e.dataItem['saved_assigned_quantity'] : 1;
                    item['qty_valid'] = e.value >= minQty
                    item[e.field] = e.value
                } else {
                    item[e.field || ''] = e.value;
                    if (e.field === licenseEndDateKey || e.field === licenseStartDateKey) {
                        item = validateDates(item,  minDate, maxDate, headerInfo.minimum_duration)
                    }
                }

                //Update unsaved changes
                if(e.field !== selectID){
                    unsavedChanges.add(item[uniqueID])
                    setUnsavedChanges(unsavedChanges)
                }
            }
            return item;
        });
        setItems([...newData]);
    };

    //calender date selector popover
    const [anchor, setAnchor] = useState(null);
    const [visible, setVisible] = useState(false);
    const [fieldName, setFieldName] = useState("");

    const changeAllDates = (field, value) => {
        let newData = items.map(item => {
            if (item[selectID]) {

                unsavedChanges.add(item[uniqueID])
                setUnsavedChanges(unsavedChanges)

                item[field] = value
                return validateDates(item, minDate, maxDate, headerInfo.minimum_duration)
            }
            return item
        })
        setItems(newData)
    }

    const onMouseOut = (ev) => {
        let isChildEl = ev.currentTarget.contains(ev.relatedTarget);
        if (!isChildEl) setVisible(false);
    };

    return (
        <>
            <Popover
                className="ksm-header-popover"
                show={visible}
                anchor={anchor}
                position={'bottom'}
                style={{padding: "none"}}
            >
                <div onMouseOut={onMouseOut}>
                    <Calendar
                        onChange={(e) => {
                            changeAllDates(fieldName, e.value)
                            setVisible(false);
                        }}
                        min={minDurationDate}
                        max={maxDate}
                    />
                </div>
            </Popover>
            <DateGridContext.Provider
                value={{
                    visible,
                    setVisible,
                    anchor,
                    setAnchor,
                    setFieldName,
                    items,
                }}>
                <GenericSelectGrid
                    className={'fsa-selected-product-grid'}
                    items={items}
                    setItems={setItems}
                    uniqueID={uniqueID}
                    gridState={gridState}
                    setGridState={setGridState}
                    initialGridState={initialGridState}
                    selectHeaderCheck={selectHeaderCheck}
                    setSelectHeaderCheck={setSelectHeaderCheck}
                    onItemChange={onItemChange}
                >
                    <GridColumn
                        field={productNumKey}
                        title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                        columnMenu={ColumnMenu}
                        cell={NoWrapCell}
                    />
                    <GridColumn
                        field="description"
                        title={localization.toLanguageString(descriptionKey, mainMessages[siteLanguageDefault][descriptionKey])}
                        columnMenu={ColumnMenu}
                    />
                    <GridColumn
                        field="quantity"
                        title={localization.toLanguageString(qtyKey, mainMessages[siteLanguageDefault][qtyKey])}
                        cell={SelectedQtyCell}
                        sortable={false}
                    />
                    <GridColumn
                        field="license_type"
                        title={localization.toLanguageString(licenseTypeKey, mainMessages[siteLanguageDefault][licenseTypeKey])}
                        cell={LicenseTypeCell}
                        sortable={false}
                    />
                    <GridColumn
                        field={licenseStartDateKey}
                        title={localization.toLanguageString(startDateKey, mainMessages[siteLanguageDefault][startDateKey])}
                        // headerCell={SelectDateHeaderCell}
                        cell={StartDatePickerCell}
                        sortable={false}
                    />
                    <GridColumn
                        field={licenseEndDateKey}
                        title={localization.toLanguageString(endDateKey, mainMessages[siteLanguageDefault][endDateKey])}
                        headerCell={SelectDateHeaderCell}
                        cell={EndDatePickerCell}
                        sortable={false}
                    />
                    <GridColumn
                        field="total_cost"
                        title={localization.toLanguageString(totalKey, mainMessages[siteLanguageDefault][totalKey])}
                        cell={TotalCell}
                        sortable={false}
                    />
                    <GridColumn
                        field={'status_display'}
                        title={localization.toLanguageString(statusKey, mainMessages[siteLanguageDefault][statusKey])}
                        cell={StatusCell}
                        sortable={false}
                    />
                    <GridColumn
                        field={null}
                        title={" "}
                        cell={(props) =>
                            <DeleteCell
                                showGenericError={showGenericError}
                                setShowGenericError={setShowGenericError}
                                {...props}
                            />
                        }
                        sortable={false}
                    />
                </GenericSelectGrid>
            </DateGridContext.Provider>
        </>
    )
}